import { useEffect, useMemo, useState } from "react";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/pages/admin/AdminLayout";
import { LeadRow } from "@/pages/admin/LeadRow";
import { LeadDialog } from "@/pages/admin/LeadDialog";
import { errMsg, LEAD_STATUSES, useApi } from "@/lib/api";

export default function Leads() {
  const api = useApi();
  const [leads, setLeads] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api("get", "/admin/leads").then(setLeads);
  }, [api]);

  const update = async (id, changes) => {
    try {
      const lead = await api("patch", `/admin/leads/${id}`, changes);
      setLeads((ls) => ls.map((l) => (l.id === id ? lead : l)));
      setSelected((s) => (s?.id === id ? lead : s));
      toast.success("Lead aggiornato");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (id) => {
    await api("delete", `/admin/leads/${id}`);
    setLeads((ls) => ls.filter((l) => l.id !== id));
    setSelected(null);
    toast.success("Lead eliminato");
  };

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return (leads || []).filter((l) => (status === "all" || l.status === status) &&
      [l.name, l.phone, l.email, l.address, l.city].some((v) => (v || "").toLowerCase().includes(t)));
  }, [leads, q, status]);

  return (
    <div data-testid="admin-leads-page">
      <PageHeader title="Lead & CRM" subtitle={leads ? `${leads.length} contatti totali` : ""} />
      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input data-testid="leads-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, telefono, città…"
            className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-emerald-500" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="leads-status-filter" className="h-10 w-full rounded-xl bg-white sm:w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {!leads ? <div className="p-10"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> : (
          <table data-testid="admin-leads-table" className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Data", "Nome", "Telefono", "Località", "Stabile", "Impianto", "Stato", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map((l) => <LeadRow key={l.id} lead={l} onStatus={(s) => update(l.id, { status: s })} onOpen={() => setSelected(l)} />)}
              {filtered.length === 0 && <tr><td colSpan={8} data-testid="leads-empty" className="px-4 py-12 text-center text-slate-500">Nessun lead trovato. I lead arrivano dal calcolatore Apollo.AI quando un visitatore lascia nome e telefono.</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      <LeadDialog lead={selected} onClose={() => setSelected(null)} onSave={update} onDelete={remove} />
    </div>
  );
}
